import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { ICreateShopObject } from 'models/shop/createshop';
import { IStore } from 'redux/IStore';
import { Field, reduxForm, getFormSyncErrors } from 'redux-form';
import { BottomButton, Button, ErrorSummary, PersonItem } from 'components';
import { StaffPreviewItemCard } from 'components/ItemList/ItemCards';
import { PortalPageSection } from 'components/PortalStructure';
import { RouteComponentProps, browserHistory } from 'react-router';
import { PortalSelectMenu, PortalUserSelect } from 'components/FormControls';
import { IImageData, InjectedFormProps, INodeDto, ITranslateProps } from 'models/common';
import { IManageShop } from 'models/shop/manageshop';
import { ICreateShopProfile, IShopProfile } from 'models/shop/shopprofile';
import { clearFormData } from 'redux/modules/shop/manageshop';
import { Dispatch } from 'react-redux';
import { IUserCard } from 'models/user/usercard';
import { IModalAction } from 'models/modal';
import { actions as notifActions } from 'redux-notifications';
import { IOfficeMember, IShopAdmin } from 'models/shop/members';
import { editMember } from 'redux/modules/shop/helpers';
import { v4 as uuidv4 } from 'uuid';
import { idFromEntityId } from 'helpers/common';
import { cropAndSaveFormImage } from 'redux/modules/image';
import { scrollErrorIntoView } from 'helpers/validationattributes';
import { SortEndHandler, SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import renderIcon from 'helpers/renderIcon';
const { connect } = require('react-redux');

interface IOwnProps {
	readOnlyAdmins?: boolean;
	toggleReadOnlyAdmins?: () => void;
	readOnlyStaff?: boolean;
	toggleReadOnlyStaff?: () => void;
	redux: 'createShop' | 'manageShop';
	onSubmit?: ((form: ICreateShopObject) => void) | ((form: ICreateShopObject<IShopProfile>) => void);
	previousPage?: () => void;
	showModal?: (modalType: string, modalProps: any) => IModalAction;
	closeModal?: () => IModalAction;
	customMemberClassName?: string;
}

interface IRouterParams {}

export function arrayMove(array, fromIndex, toIndex) {
	const startIndex = fromIndex < 0 ? array.length + fromIndex : fromIndex;

	if (startIndex >= 0 && startIndex < array.length) {
		const endIndex = toIndex < 0 ? array.length + toIndex : toIndex;

		const [item] = array.splice(fromIndex, 1);
		array.splice(endIndex, 0, item);
	}

	return array;
}

interface IProps
	extends IOwnProps,
		ReturnType<typeof stateToProps>,
		ReturnType<typeof dispatchToProps>,
		ITranslateProps,
		RouteComponentProps<IRouterParams, {}>,
		InjectedFormProps<ICreateShopObject | IManageShop> {
	showModal?: (modalType: string, modalProps: any) => IModalAction;
	closeModal?: () => IModalAction;
	cropAndSaveFormImage?: (image: IImageData) => Promise<any>;
}
@connect(
	() => ({}),
	(dispatch) => ({
		cropAndSaveFormImage: (image: IImageData) => dispatch(cropAndSaveFormImage(image)),
	}),
)
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ShopMembersForm extends React.Component<IProps, {}> {
	constructor(props) {
		super(props);

		this.onSubmit = this.onSubmit.bind(this);
		this.addMember = this.addMember.bind(this);
		this.removeMember = this.removeMember.bind(this);
		this.editMember = this.editMember.bind(this);
		this.startDraggingMember = this.startDraggingMember.bind(this);
		this.stopDraggingMember = this.stopDraggingMember.bind(this);

		this.formRef = React.createRef();
	}

	private formRef;

	public componentDidMount(): void {
		if (this.props?.redux === 'manageShop' && this.props?.currentFormValues?.shop && this.props?.location?.query?.idx) {
			const officeIndex = this.props?.location?.query?.idx || 0;
			this.props.change('shop.tmpTimesLocation', `${officeIndex}`);
		}
	}

	public componentWillUnmount(): void {
		this.props.clearFormData();
	}

	public componentDidUpdate(prevProps: IProps) {
		if (Boolean(prevProps.readOnlyAdmins) !== Boolean(this.props.readOnlyAdmins)) {
			if (this.props.resetSection) {
				this.props.resetSection('shop.admins');
			} else {
				this.props.reset();
			}
		}
		if (Boolean(prevProps.readOnlyStaff) !== Boolean(this.props.readOnlyStaff)) {
			if (this.props.resetSection) {
				this.props.resetSection('shop.offices');
			} else {
				this.props.reset();
			}
		}
	}

	public onSubmit(form: ICreateShopObject) {
		if (this.props?.redux === 'manageShop') {
			browserHistory.push(
				`/shop/${this.shop.id}/edit/members?idx=${(this.props.currentFormValues.shop as ICreateShopProfile)
					?.tmpTimesLocation}`,
			);
		}
		this.props.onSubmit(JSON.parse(JSON.stringify(form)));
	}

	private mapErrorKeyToMessage(key: string, t: (key: string) => string) {
		switch (key) {
			case 'location':
				return t('Shop_ManageShop_General_Label_Location');
			default:
				return key;
		}
	}

	private renderOfficeSelect() {
		const { t, currentFormValues } = this.props;
		const offices = currentFormValues.shop?.offices || [];
		const officesOptions = offices.map((l, index) => ({
			key: String(index),
			value: l.location,
		}));

		return offices.length > 1 ? (
			<Field
				name="shop.tmpTimesLocation"
				component={PortalSelectMenu}
				type="text"
				items={officesOptions}
				multi={false}
				isRequired={true} // To remove the blank option
				label={t('Shop_ManageShop_Location_Label_SalonLocation')}
				searchable={false}
			/>
		) : null;
	}

	private get officeIdx(): number {
		const { currentFormValues } = this.props;
		return Number((currentFormValues.shop as ICreateShopProfile).tmpTimesLocation) || 0;
	}

	private get office() {
		const { currentFormValues } = this.props;
		return currentFormValues.shop?.offices ? currentFormValues.shop.offices[this.officeIdx] : null;
	}

	private get shop() {
		const { currentFormValues } = this.props;
		return currentFormValues.shop;
	}

	private DragHandle = SortableHandle(() => (
		<div className="dragIconContainer d-flex flex-column justify-content-between justify-content-md-end align-items-md-end">
			{renderIcon('hamburger')}
		</div>
	));

	private SortableMemberMobile = SortableElement(({ s, readOnly, position, idx }) => {
		return (
			<div className="staffMembers__member">
				{!this.props.readOnlyStaff ? <this.DragHandle /> : null}
				<span className="staffMembers__member__number">{idx + 1}</span>
				<div className="staffMembers__member__nameContainer">
					<span className="staffMembers__member__name">{s.name}</span>
					{readOnly ? null : (
						<button type="button" className="btnIcon round" onClick={() => this.removeMember(position, idx)}>
							<img src={require('containers/Html/icons/delete_2.svg')} />
						</button>
					)}
				</div>
			</div>
		);
	});

	private SortableMemberDesktop = SortableElement(({ s, readOnly, idx }) => {
		return (
			<div className="staffMembers__desktopMemberContainer">
				{!this.props.readOnlyStaff ? <this.DragHandle /> : null}
				<StaffPreviewItemCard
					item={s}
					customProps={{
						readOnly: readOnly,
						editMember: (staffMemberData) => {
							this.editMember(idx, staffMemberData as IOfficeMember);
						},
						removeMember: () => {
							this.removeMember('staff', idx);
						},
						customClassName: this.props?.customMemberClassName
							? this.props.customMemberClassName
							: 'staffMembers__desktopMember',
					}}
				/>
			</div>
		);
	});

	private SortableMembers = SortableContainer(({ children, position }) => {
		return <div className={`staffMembers__container ${position === 'staff' ? 'd-md-none' : ''}`}>{children}</div>;
	});

	private SortableMembersDesktop = SortableContainer(({ children }) => {
		return <div className="staffMembers__container d-none d-md-flex d-row flex-wrap">{children}</div>;
	});

	private startDraggingMember() {
		document.body.style.cursor = 'grabbing';
	}

	private stopDraggingMember(a: Parameters<SortEndHandler>[0]) {
		const { oldIndex, newIndex } = a;

		const usersList = JSON.parse(JSON.stringify(this.props.currentFormValues.shop.offices[this.officeIdx].staff));
		usersList.sort((a, b) => a.order - b.order);
		usersList.forEach((_, i) => {
			usersList[i].order = i;
		});

		arrayMove(usersList, oldIndex, newIndex).forEach((_, i) => (usersList[i].order = i));
		this.props.change(`shop.offices[${this.officeIdx}].staff`, usersList);
		document.body.style.cursor = 'default';
	}

	private addMember(position: 'admins' | 'staff') {
		const { userPreferences, change } = this.props;
		return (_: React.ChangeEvent<any>, user: INodeDto) => {
			if (!user || !this.shop) {
				console.warn('No user or shop, cannot add role', { user, shop: this.shop });
				return;
			}
			if (position === 'admins') {
				const users = [...(this.shop[position] || [])];
				const copy = users.find((l) => l.entityId === user.entityId);
				const isCurrentUser = JSON.parse(user.entityId).id === userPreferences.id;

				// If shop owner don't add them to admins because it creates extra permissions unnecessarily (during shop creation)
				// Works on edit members page aswell so add check from which form the data comes from if necessary
				if (isCurrentUser) {
					return;
				}

				if (copy) {
					return;
				}
				users.push(user);
				change('shop.' + position, users);
				change('shop.tmpMember', '');
				requestAnimationFrame(() => {
					change('shop.tmpMember', '');
					this.forceUpdate();
				});
			} else {
				if (!this.office) {
					console.warn('No office, cannot add staff');
					return;
				}
				const members = [...(this.office[position] || [])] as IOfficeMember[];
				const copy = members.find((l) => l.userId === idFromEntityId(user.entityId));
				if (copy) {
					console.warn('User already member');
					return;
				}
				members.push({
					userId: idFromEntityId(user.entityId),
					name: user.name,
					picture: user.picture,
					id: uuidv4(),
					order: members.length,
				});
				change('shop.offices[' + this.officeIdx + '].' + position, members);
				change('shop.offices[' + this.officeIdx + '].tmpMember', '');
				requestAnimationFrame(() => {
					change('shop.offices[' + this.officeIdx + '].tmpMember', '');
					this.forceUpdate();
				});
			}
		};
	}

	private removeMember(position: 'admins' | 'staff', index: number) {
		const { change } = this.props;
		if (!this.shop) {
			return;
		}
		if (position === 'admins') {
			const users = [...(this.shop[position] || [])];
			users.splice(index, 1);
			change('shop.' + position, users);
		} else {
			if (!this.office) {
				return;
			}
			const users = [...(this.office[position] || [])];
			users.splice(index, 1);
			change('shop.offices[' + this.officeIdx + '].' + position, users);
		}
	}

	private editMember(staffMemberIdx: number, staffMemberData: IOfficeMember) {
		const { change } = this.props;

		this.props.showModal('EditStaffMemberModal', {
			// Change this when staff member data model is changed
			initialValues: staffMemberData,
			saveImage: (image: IImageData) => this.props.cropAndSaveFormImage(image),

			onSubmitCallback: (formData) => {
				return new Promise((resolve, reject) => {
					// SEND DATA TO API HERE
					const path = `shop.offices[${this.officeIdx}].staff[${staffMemberIdx}]`;

					const success = () => {
						change(`${path}.name`, formData.name);
						//change(`${path}.name`, formData.nickname)
						change(`${path}.jobTitle`, formData.jobTitle);
						change(`${path}.description`, formData.description);
						change(`${path}.picture`, formData.picture);

						this.formRef.current?.dispatchEvent(new Event('submit'));

						this.props.showNotification(this.props.t('Shop_ManageShop_ManageStaffForm_success'), 'success');
						this.props.closeModal();

						resolve(null);
					};

					if (staffMemberData.id) {
						this.props.editMember(Object.assign({}, staffMemberData, formData) as IOfficeMember, success, (e) => {
							console.error(e);
							this.props.showNotification(this.props.t('Shop_ManageShop_ManageStaffForm_error'), 'error');
							reject();
						});
					} else {
						success();
					}
				});
			},
			closeModal: this.props.closeModal,
		});
	}

	private renderUsersSection(position: 'admins' | 'staff', readOnly?: boolean) {
		const { t, fetchDefault } = this.props;
		if (!this.office && position === 'staff') {
			return;
		}
		if (!this.shop && position === 'admins') {
			return;
		}
		const usersList = position === 'staff' ? this.office[position] : this.shop[position];
		const tmpUserName = position === 'staff' ? 'shop.offices[' + this.officeIdx + '].tmpMember' : 'shop.tmpMember';
		const sortableMembersElementsMobile: JSX.Element[] = [];
		const sortableMembersElementsDesktop: JSX.Element[] = [];

		if (usersList) {
			usersList
				.sort((a, b) => a.order - b.order)
				.forEach((s, idx) => {
					sortableMembersElementsMobile.push(
						<this.SortableMemberMobile
							key={`item-${s.id}`}
							s={s}
							readOnly={readOnly}
							position={position}
							idx={idx}
							index={idx}
						/>,
					); // Keys are very important for react sortable hoc
					sortableMembersElementsDesktop.push(
						<this.SortableMemberDesktop
							key={`desktopItem-${s.id}`}
							s={s}
							readOnly={readOnly}
							idx={idx}
							index={idx}
							disabled={this.props.readOnlyStaff}
						/>,
					);
				});
		}

		return (
			<>
				{position === 'staff' && this.renderOfficeSelect()}
				{readOnly ? null : (
					<div className="row align-items-center">
						<p className="col-xs-12 col-md-3">
							{t(
								position === 'admins'
									? 'Shop_ManageShop_Members_Label_AddAdmin'
									: 'Shop_ManageShop_Members_Label_AddStaff',
							)}
						</p>
						<div className="col-xs-12 col-md-9">
							<Field
								className="rounded"
								name={tmpUserName}
								onChange={this.addMember(position)}
								fetchDefault={fetchDefault}
								component={PortalUserSelect}
								type="text"
								label={t('PortalPage_Section_Manage_Common_SearchUser')}
							/>
						</div>
					</div>
				)}

				{position === 'staff' && (
					<div className="d-none d-md-flex d-row flex-wrap">
						<this.SortableMembersDesktop
							axis="xy"
							onSortStart={() => this.startDraggingMember()}
							onSortEnd={(a) => this.stopDraggingMember(a)}
							useDragHandle={true}
							lockToContainerEdges={true}
						>
							{sortableMembersElementsDesktop}
						</this.SortableMembersDesktop>
					</div>
				)}

				{position === 'staff' ? (
					<this.SortableMembers
						axis="y"
						lockAxis="y"
						useDragHandle={true}
						onSortEnd={(a) => this.stopDraggingMember(a)}
						position={position}
						key={`office${this.officeIdx}`}
					>
						{sortableMembersElementsMobile}
					</this.SortableMembers>
				) : (
					<dl className={`list`}>
						{(usersList as any)?.map((s: IOfficeMember | INodeDto | IUserCard | IShopAdmin, index) => (
							<React.Fragment key={s.name}>
								{' '}
								{/* Id values are not present on add admin */}
								<dt>{index + 1}</dt>
								<dd>
									<span>{s.name}</span>
									{readOnly ? null : (
										<button type="button" className="btnIcon round" onClick={() => this.removeMember(position, index)}>
											<img src={require('containers/Html/icons/delete_2.svg')} />
										</button>
									)}
								</dd>
							</React.Fragment>
						))}
					</dl>
				)}

				{readOnly ? null : (
					<Button className="formButton" type="submit">
						{t('BSPortal_Common_Save')}
					</Button>
				)}
			</>
		);
	}

	public render() {
		const {
			handleSubmit,
			t,
			formErrors,
			submitFailed,
			location,
			toggleReadOnlyAdmins,
			readOnlyAdmins,
			toggleReadOnlyStaff,
			readOnlyStaff,
			currentFormValues,
			userPreferences,
		} = this.props;

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			error: this.props.createShop ? this.props.createShop.error : false,
			message: this.props.createShop ? this.props.createShop.message : null,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		const ownerData = this.props.redux === 'createShop' ? userPreferences : currentFormValues?.shop?.ownerData;

		return (
			<form onSubmit={handleSubmit(this.onSubmit)} ref={this.formRef}>
				<h1 className="text-center pageTitle">{t('Shop_ManageShop_Members_Title')}</h1>
				<div className="profileError">
					<ErrorSummary {...errorSummaryProps} />
				</div>
				<PortalPageSection
					title={t('Shop_ManageShop_Members_Label_Owner')}
					description={t('Shop_ManageShop_Members_Description_Owner')}
					variant={toggleReadOnlyAdmins ? 'dark' : 'normal'}
				>
					{ownerData ? <PersonItem user={ownerData} /> : null}
				</PortalPageSection>
				<PortalPageSection
					title={t('Shop_ManageShop_Members_Label_Admins')}
					description={t('Shop_ManageShop_Members_Description_Admins')}
					readOnly={readOnlyAdmins}
					toggleEdit={toggleReadOnlyAdmins}
				>
					{this.renderUsersSection('admins', readOnlyAdmins)}
				</PortalPageSection>
				<PortalPageSection
					title={t('Shop_ManageShop_Members_Label_Staff')}
					description={t('Shop_ManageShop_Members_Description_Staff')}
					readOnly={readOnlyStaff}
					toggleEdit={toggleReadOnlyStaff}
				>
					{this.renderUsersSection('staff', readOnlyStaff)}
				</PortalPageSection>
				<BottomButton type="submit">{t('BSPortal_Common_Save')}</BottomButton>
			</form>
		);
	}
}

const cachedComponents: { [key: string]: ReturnType<typeof createConnected> } = {};

type FormName = typeof c.FORM_NAME_CREATE_SHOP | typeof c.FORM_NAME_MANAGE_SHOP;

const stateToProps = (formName: FormName, state: IStore, ownProps: IOwnProps) => {
	let currentFormValues: ICreateShopObject | IManageShop = null;
	const shopObj = ownProps.redux === 'createShop' ? state[ownProps.redux].createShopObj : state[ownProps.redux];
	if (shopObj.shop && !(shopObj.shop as ICreateShopProfile).tmpTimesLocation) {
		(shopObj.shop as ICreateShopProfile).tmpTimesLocation = '0';
	}

	if (formName === 'manageshop' || shopObj?.shop?.offices?.length) {
		const defaultOfficeIndex = shopObj?.shop?.offices?.findIndex((off) => off.isDefault) || 0;
		(shopObj.shop as ICreateShopProfile).tmpTimesLocation = defaultOfficeIndex.toString();
	}

	if (state.form[formName] && state.form[formName].values) {
		currentFormValues = state.form[formName].values;
	} else {
		currentFormValues = shopObj;
	}

	return {
		createShop: state[ownProps.redux],
		initialValues: shopObj,
		currentFormValues,
		formErrors: getFormSyncErrors(formName)(state),
		fetchDefault: state.fetchDefault,
		userPreferences: state.userPreferences,
	};
};

const dispatchToProps = (dispatch: Dispatch<IStore>) => ({
	editMember: (member: IOfficeMember, success, error) =>
		dispatch((_dispatch, getState: () => IStore) => editMember(getState, member).then(success).catch(error)),
	clearFormData: () => dispatch(clearFormData()),
	showNotification: (message: string, kind: string, dismissAfter = 3000) =>
		dispatch(notifActions.notifSend({ message, kind, dismissAfter })),
});

function createConnected(formName: FormName) {
	let ShopMembers = reduxForm({
		form: formName,
		destroyOnUnmount: formName === c.FORM_NAME_MANAGE_SHOP,
		forceUnregisterOnUnmount: true,
		enableReinitialize: formName === c.FORM_NAME_MANAGE_SHOP,
		onSubmitFail: () => scrollErrorIntoView(),
	})(ShopMembersForm) as React.FC<IOwnProps>;

	ShopMembers = connect(
		(state: IStore, ownProps: IOwnProps) => stateToProps(formName, state, ownProps),
		dispatchToProps,
	)(ShopMembers);

	return ShopMembers;
}

export function getConnectedShopMembers(formName: typeof c.FORM_NAME_CREATE_SHOP | typeof c.FORM_NAME_MANAGE_SHOP) {
	if (!(formName in cachedComponents)) {
		cachedComponents[formName] = createConnected(formName);
	}
	return cachedComponents[formName];
}

export const ShopMembers = getConnectedShopMembers(c.FORM_NAME_CREATE_SHOP);
